import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, Row } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import {
  LogoFacebook,
  LogoTwitter,
  LogoLinkedIn,
  LogoYoutube,
  LogoInstagram,
  LogoKakaoStory,
  LogoWhatsApp,
} from '../assets'

const mapPlatformButtonProps = (platforms, t, analytics) => {
  const target = '_blank'
  const config = {
    linkedin: {
      label: t('social.linkedin.label', 'Linkedin'),
      Icon: LogoLinkedIn,
      target,
      onClick: () => getOnClick(analytics, 'Linkedin'),
      // to: 'https://www.linkedin.com/', // this is suffixed with either /in/... or /company/...
    },
    facebook: {
      label: t('social.facebook.label', 'Facebook'),
      Icon: LogoFacebook,
      target,
      onClick: () => getOnClick(analytics, 'Facebook'),
      // to: 'https://www.facebook.com/',
    },
    instagram: {
      label: t('social.instagram.label', 'Instagram'),
      Icon: LogoInstagram,
      target,
      onClick: () => getOnClick(analytics, 'Instagram'),
      // to: 'https://www.instagram.com/',
    },
    twitter: {
      label: t('social.x.label', 'X'),
      Icon: LogoTwitter,
      target,
      onClick: () => getOnClick(analytics, 'Twitter'),
      // to: 'https://www.x.com/',
    },
    youtube: {
      label: t('social.youtube.label', 'YouTube'),
      Icon: LogoYoutube,
      target,
      onClick: () => getOnClick(analytics, 'YouTube'),
      // to: 'https://youtube.com/', // this is suffixed with either /c/..., /channel/..., or /user/...
    },
    kakaoStory: {
      label: t('social.kakao-story.label', 'Kakao Story'),
      Icon: LogoKakaoStory,
      target,
      onClick: () => getOnClick(analytics, 'Kakao-Story'),
      // to: 'https://story.kakao.com/',
    },
    whatsApp: {
      label: t('social.whatsApp.label', 'WhatsApp'),
      Icon: LogoWhatsApp,
      target,
      onClick: () => getOnClick(analytics, 'WhatsApp'),
      // to: https://whatsapp.com or https://wa.me,
    },
  }

  const networks = []
  Object.keys(platforms).forEach((platform) => {
    if (platforms[platform]) {
      const network = config[platform]
      network.to = platforms[platform]
      networks.push(network)
    }
  })
  return networks
}

function getOnClick(analytics, name) {
  if (analytics) {
    trackEvent({
      link_name: analytics.link_name ? `${analytics.link_name}: ${name}` : name,
      link_page: analytics.link_page,
      link_app: analytics.link_app || 'fs',
    })
  }
}

export default function SocialConnect({
  platforms = {},
  useIconButtons = false,
  iconButtonEmphasis = 'high',
  wrap,
  gutters = 'sm',
  guttersY = 'sm',
  alignX = 'start',
  analytics = {},
  stacked = false,
  color = 'blue',
}) {
  const { t } = useTranslation()
  const buttons = mapPlatformButtonProps(platforms, t, analytics)

  const buttonsSection = buttons.map(({ label, Icon, ...buttonProps }) => {
    return useIconButtons ? (
      <div key={`${label}-wrapper`}>
        <IconButton
          key={label}
          Icon={Icon}
          title={label}
          emphasis={iconButtonEmphasis}
          color={color}
          {...buttonProps}
        />
      </div>
    ) : (
      <div key={`${label}-wrapper`}>
        <Button key={label} Icon={Icon} color={color} {...buttonProps}>
          {label}
        </Button>
      </div>
    )
  })

  return stacked ? (
    <>{buttonsSection}</>
  ) : (
    <Row wrap={wrap} gutters={gutters} alignX={alignX} guttersY={guttersY}>
      {buttonsSection}
    </Row>
  )
}

SocialConnect.propTypes = {
  /** Displays the buttons as IconButtons. */
  useIconButtons: PropTypes.bool,
  /** If you need black icon buttons, because you're header/footer */
  iconButtonEmphasis: PropTypes.oneOf(['medium', 'low']),

  /** The key value pair for the company or users social platforms. */
  platforms: PropTypes.shape({
    linkedin: PropTypes.string,
    youtube: PropTypes.string,
    instagram: PropTypes.string,
    facebook: PropTypes.string,
    twitter: PropTypes.string,
    kakaoStory: PropTypes.string,
    whatsApp: PropTypes.string,
  }).isRequired,

  /** Display social media in a vertical stack (ignores wrap, gutters, alignX) */
  stacked: PropTypes.bool,

  /** Row gutters */
  gutters: PropTypes.oneOf(['lg', 'md', 'sm', 'xs', 'xxs', 'nano']),

  /** Row guttersY */
  guttersY: PropTypes.oneOf(['lg', 'md', 'sm', 'xs', 'xxs', 'nano', 'none']),

  /** Whether the Row should wrap its content. If the content doesn't wrap it will use flex-box to keep the content on one line. */
  wrap: PropTypes.bool,

  /** Horizontal alignment of children */
  alignX: PropTypes.oneOf(['start', 'center', 'end']),

  /** The analytics metrics to send to Adobe Analytics for clicks on buttons to open a share dialog overlay. Analytics metrics are only sent if this property is specified.  */
  analytics: PropTypes.shape({
    /** OPTIONAL: The name of the link or button that was clicked to launch the menu */
    link_name: PropTypes.string,

    /** REQUIRED: The name of the page where the SocialDialog is displayed. */
    link_page: PropTypes.string,

    /** OPTIONAL: The name of the web app that served the page. If not specified, the product prop value will be used. */
    link_app: PropTypes.string,
  }),

  /** Button or IconButton color */
  color: PropTypes.oneOf(['blue', 'gray']),
}
